"use client";

import { FetchInput } from "@/fetch-ui/FetchInput";
import { CountryDialCode, getCountriesDialCodes } from "@/lib/db/countries-dial-codes";
import { nanpCountryDialCodeId } from "@/lib/phone/nanp-country";
import { findCountryByDialCode } from "@/lib/phone/phone-utils";
import { useEffect, useState } from "react";
import { CountryDialCodeDropdown } from "./CountryDialCodeDropdown";

/** Off-screen styling shared by the hidden autofill mirror inputs. */
const HIDDEN_AUTOFILL_MIRROR_CLASS = "absolute -left-[9999px] h-px w-px opacity-0 pointer-events-none";

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  countryDialCodeId?: number | null;
  onCountryDialCodeIdChange?: (id: number | null) => void;
  placeholder?: string;
  id?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export const PhoneNumberInput = ({
  value,
  onChange,
  countryDialCodeId,
  onCountryDialCodeIdChange,
  placeholder = "(123) 456-7890",
  id,
  className = "",
  required = false,
  disabled = false,
  error = false,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: PhoneNumberInputProps) => {
  const [countries] = useState<CountryDialCode[]>(() => getCountriesDialCodes());
  const [selectedCode, setSelectedCode] = useState<string>("US");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const isLoading = false;

  useEffect(() => {
    if (!isLoading && countries.length > 0) {
      const defaultCode = "US";
      const US_COUNTRY_ID = 235; // Hardcoded ID for US in countries_dial_codes table
      // Always try to find US first by code, then by ID, fallback to first country if US not found
      const defaultCountry =
        countries.find((c) => c.code === defaultCode) || countries.find((c) => c.id === US_COUNTRY_ID) || countries[0];

      // If countryDialCodeId is provided, use it to find the country
      if (countryDialCodeId) {
        const country = countries.find((c) => c.id === countryDialCodeId);
        if (country) {
          setSelectedCode(country.code);
          // Value should be just digits (phone number without dial code)
          if (value) {
            const digitsOnly = value.replace(/\D/g, "");
            setPhoneNumber(digitsOnly);
          } else {
            setPhoneNumber("");
          }
          return;
        }
      }

      // No countryDialCodeId or not found - default to US
      // Try to parse value if it has a dial code, otherwise default to US
      if (value) {
        const trimmed = value.trim();

        if (trimmed.startsWith("+")) {
          // Capture all leading digits after the "+" so we can handle E.164
          // format without separators (e.g. "+447700900123" — UK).
          const digitMatch = trimmed.match(/^\+(\d+)/);
          if (digitMatch) {
            const leadingDigits = digitMatch[1];
            // Country dial codes are 1-4 digits. Try the longest prefix first
            // so e.g. "+1242" (Bahamas) wins over "+1" (US) when both exist.
            let country: CountryDialCode | undefined;
            let matchedCodeLength = 0;
            for (let len = Math.min(4, leadingDigits.length); len >= 1; len--) {
              const candidate = `+${leadingDigits.slice(0, len)}`;
              const found = countries.find((c) => {
                const dialCodeStr = String(c.dial_code || "");
                const normalized = dialCodeStr.startsWith("+") ? dialCodeStr : `+${dialCodeStr}`;
                return normalized === candidate;
              });
              if (found) {
                // "+1" is shared by the US and Canada; the area code decides.
                const nanpId = candidate === "+1" ? nanpCountryDialCodeId(leadingDigits.slice(len)) : null;
                country = (nanpId && countries.find((c) => c.id === nanpId)) || found;
                matchedCodeLength = len;
                break;
              }
            }
            if (country) {
              setSelectedCode(country.code);
              const localPart = trimmed.slice(matchedCodeLength + 1);
              setPhoneNumber(localPart.replace(/\D/g, ""));
              if (onCountryDialCodeIdChange) {
                onCountryDialCodeIdChange(country.id);
              }
            } else {
              // Dial code not found, default to US
              setSelectedCode(defaultCode);
              setPhoneNumber(trimmed.replace(/\D/g, ""));
              if (onCountryDialCodeIdChange) {
                onCountryDialCodeIdChange(US_COUNTRY_ID);
              }
            }
          } else {
            // "+" with no digits — invalid, default to US
            setSelectedCode(defaultCode);
            setPhoneNumber(trimmed.replace(/\D/g, ""));
            if (onCountryDialCodeIdChange) {
              onCountryDialCodeIdChange(US_COUNTRY_ID);
            }
          }
        } else {
          // Just digits, no dial code - default to US
          const digitsOnly = trimmed.replace(/\D/g, "");
          setSelectedCode(defaultCode);
          setPhoneNumber(digitsOnly);
          if (onCountryDialCodeIdChange) {
            onCountryDialCodeIdChange(US_COUNTRY_ID);
          }
        }
      } else {
        // No value - default to US
        setSelectedCode(defaultCode);
        setPhoneNumber("");
        if (onCountryDialCodeIdChange) {
          onCountryDialCodeIdChange(US_COUNTRY_ID);
        }
      }
    }
  }, [value, countryDialCodeId, isLoading, countries, onCountryDialCodeIdChange]);

  const handleCodeChange = (code: string) => {
    setSelectedCode(code);
    const country = countries.find((c) => c.code === code);
    if (!country) return;

    if (onCountryDialCodeIdChange) {
      // The country has its own channel. Do NOT also re-emit the phone here: a
      // caller that merges phone + country into one value (e.g. FormPhoneField)
      // rebuilds that value from its current props, so a second `onChange` fired
      // in the same tick would carry the STALE country id and clobber this
      // selection — making the country picker appear to do nothing. The phone is
      // unchanged by a country switch, so there is nothing to re-emit.
      onCountryDialCodeIdChange(country.id);
    } else if (phoneNumber) {
      // No country channel — re-emit digits so an onChange-only caller still gets
      // a value (it can't track the country anyway).
      onChange(phoneNumber);
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/\D/g, "");
    setPhoneNumber(inputValue);

    // Only return the phone number digits, not the dial code
    onChange(inputValue);
  };

  // Build full international value (e.g. "+15551234567") for the hidden autofill
  // mirror input. Browsers fill this with the full stored number; the country is
  // then resolved either by this component's useEffect (when no countryDialCodeId
  // is set) or by the FormPhoneField wrapper, which normalizes the "+CC" value.
  const selectedCountry = countries.find((c) => c.code === selectedCode);
  const dialCodeRaw = selectedCountry?.dial_code ? String(selectedCountry.dial_code) : "";
  const dialCodeNormalized = dialCodeRaw ? (dialCodeRaw.startsWith("+") ? dialCodeRaw : `+${dialCodeRaw}`) : "";
  const fullInternationalValue =
    phoneNumber && dialCodeNormalized ? `${dialCodeNormalized}${phoneNumber}` : phoneNumber;

  const handleFullTelAutofill = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Once the user has started entering their own digits, a browser autofill
    // event on this hidden mirror (which can arrive asynchronously, after
    // typing has already begun) must not stomp their in-progress edit.
    if (phoneNumber) return;
    const rawValue = e.target.value;
    if (rawValue === fullInternationalValue) return;
    // Browser autofill of a full international number — let the parser in
    // useEffect resolve the country and split out the local digits.
    if (rawValue.startsWith("+")) {
      onChange(rawValue);
    }
  };

  const handleCountryCodeAutofill = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Unlike handleFullTelAutofill, this never touches `phoneNumber` — it only
    // ever changes which country is selected — so it's safe to apply even
    // after digits have landed (e.g. the browser autofills the visible
    // national field directly, then separately exposes the dial code here).
    const rawValue = e.target.value;
    if (rawValue === dialCodeNormalized) return;
    // Some browsers/profiles expose the country code as its own field (e.g.
    // "+44") even when the main number is national-only. Map it to a country so
    // the picker reflects it; the national digits arrive via the visible input.
    // This assumes the visible `tel-national` field is national-only — if a
    // browser instead fills it with a dial-code-prefixed number, the toE164
    // helpers heal the resulting double prefix at the read boundary.
    const dialCode = rawValue.replace(/\D/g, "");
    if (!dialCode) return;
    // "+1" is shared across all NANP countries; findCountryByDialCode would pick
    // whichever sorts first, so resolve it to US (the component's default) and
    // only fall back to dial-code lookup for unambiguous international codes.
    const country = dialCode === "1" ? countries.find((c) => c.code === "US") : findCountryByDialCode(dialCode);
    if (country && onCountryDialCodeIdChange) {
      onCountryDialCodeIdChange(country.id);
    }
  };

  if (isLoading) {
    return (
      <div className={`flex gap-2 ${className}`}>
        <div className="w-[85px] h-10 bg-gray-200 rounded-lg animate-pulse" />
        <div className="flex-1 h-10 bg-gray-200 rounded-lg animate-pulse" />
      </div>
    );
  }

  if (countries.length === 0) {
    return (
      <div className={`flex gap-2 ${className}`}>
        <div className="w-[85px] h-10 border border-gray-300 rounded-lg bg-gray-100 flex items-center justify-center">
          <span className="text-xs text-gray-400">Loading...</span>
        </div>
        <FetchInput
          id={id}
          type="tel"
          autoComplete="tel-national"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          placeholder={placeholder}
          className="flex-1"
          required={required}
          disabled
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
        />
      </div>
    );
  }

  return (
    <div className={`flex gap-2 ${className}`}>
      <input
        type="tel"
        autoComplete="tel"
        name="phone"
        value={fullInternationalValue}
        onChange={handleFullTelAutofill}
        tabIndex={-1}
        aria-hidden="true"
        className={HIDDEN_AUTOFILL_MIRROR_CLASS}
      />
      <input
        type="tel"
        autoComplete="tel-country-code"
        name="tel-country-code"
        value={dialCodeNormalized}
        onChange={handleCountryCodeAutofill}
        tabIndex={-1}
        aria-hidden="true"
        className={HIDDEN_AUTOFILL_MIRROR_CLASS}
      />
      <CountryDialCodeDropdown
        countries={countries}
        selectedCode={selectedCode}
        onSelect={handleCodeChange}
        disabled={disabled}
        error={error}
      />
      <FetchInput
        id={id}
        type="tel"
        autoComplete="tel-national"
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        placeholder={placeholder}
        className="flex-1"
        required={required}
        disabled={disabled}
        error={error}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid ?? error}
      />
    </div>
  );
};
